import { getSubscriptionPlanName } from "../utils/subscriptionPlan";
import { BASE, getCsrfToken } from "./constants";

export interface PaymentMethodCard {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface PaymentMethod {
  type: string;
  card?: PaymentMethodCard;
  status: string;
  error?: string;
}

export interface SubscriptionSettings {
  user_limit: number | null;
  storage_limit: number | null;
}

export interface UserSettings extends SubscriptionSettings {
  resource_limit: number | null;
}

export type SubscriptionSettingsUpdate = Partial<SubscriptionSettings>;

export interface SubscriptionData {
  autopayment: boolean;
  plan: string;
  interval: string | null;
  price: string;
  currency: string;
  expire_at: string | null;
  options: unknown | null;
  canceled_at: string | null;
  trial_active: boolean;
  allow_trial: boolean;
  team_plan?: string | null;
  payment_method?: PaymentMethod | null;
  user_settings?: UserSettings | null;
}

export interface PriceInfo {
  amount: number;
  currency: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  monthlyPrice: PriceInfo;
  yearlyPrice: PriceInfo;
  monthlyPriceDiscounted?: PriceInfo;
  yearlyPriceDiscounted?: PriceInfo;
  discount_annual?: number;
}

export interface SubscriptionPlanRaw {
  id: string;
  name: string;
  price_original: Record<string, Record<string, number>>;
  price_discounted: Record<string, Record<string, number>> | null;
  discount_annual?: number;
  discount_temporal: number | null;
}

export function mapSubscriptionPlan(
  raw: SubscriptionPlanRaw,
  currency: string,
): SubscriptionPlan {
  const curr = currency.toLowerCase();
  return {
    id: raw.id,
    name: getSubscriptionPlanName(raw.id),
    discount_annual: raw.discount_annual,
    monthlyPrice: {
      amount: raw.price_original?.monthly?.[curr] ?? 0,
      currency: currency.toUpperCase(),
    },
    yearlyPrice: {
      amount: raw.price_original?.yearly?.[curr] ?? 0,
      currency: currency.toUpperCase(),
    },
    monthlyPriceDiscounted:
      raw.price_discounted?.monthly?.[curr] != null
        ? {
            amount: raw.price_discounted.monthly[curr],
            currency: currency.toUpperCase(),
          }
        : undefined,
    yearlyPriceDiscounted:
      raw.price_discounted?.yearly?.[curr] != null
        ? {
            amount: raw.price_discounted.yearly[curr],
            currency: currency.toUpperCase(),
          }
        : undefined,
  };
}

export interface SubscriptionInAdvance {
  subscription: { expire_at: string };
  convertion: { additional_days: number };
}

export interface SubscriptionConformance {
  conforms: boolean;
  violations: string[];
}

const getSubscription = async (): Promise<SubscriptionData> => {
  const res = await fetch(`/api/subscription/`, {
    headers: {
      "Content-type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.subscription;
};

let subscriptionPlansRequest: Promise<SubscriptionPlanRaw[]> | null = null;

const getSubscriptionPlans = (): Promise<SubscriptionPlanRaw[]> => {
  if (!subscriptionPlansRequest) {
    subscriptionPlansRequest = fetch(`${BASE}/subscription/prices/`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .catch((error) => {
        subscriptionPlansRequest = null;
        throw error;
      });
  }
  return subscriptionPlansRequest;
};

const getSubscriptionInAdvance = async ({
  plan,
  interval,
  settings,
  signal,
}: {
  plan: string;
  interval: string;
  settings?: SubscriptionSettingsUpdate;
  signal?: AbortSignal;
}): Promise<SubscriptionInAdvance> => {
  const res = await fetch("/api/subscription/convertion/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ plan, interval, settings }),
    signal,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const getSubscriptionConformance = async ({
  plan,
  settings,
}: {
  plan: string;
  settings: SubscriptionSettingsUpdate;
}): Promise<SubscriptionConformance> => {
  const res = await fetch("/api/subscription/conformance/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ new_plan: plan, settings }),
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  const result = data?.result ?? data;
  const rawViolations = Array.isArray(result)
    ? result
    : (result?.violations ?? result?.conformance ?? data?.violations ?? []);
  const violations = Array.isArray(rawViolations)
    ? rawViolations.filter(
        (violation): violation is string => typeof violation === "string",
      )
    : [];
  const conforms =
    typeof result === "boolean"
      ? result
      : (result?.is_conformant ??
        result?.conforms ??
        result?.conformant ??
        violations.length === 0);
  return { conforms: Boolean(conforms), violations };
};

const getSubscriptionPlanPrice = async ({
  plan,
  coupon,
  currency,
  interval,
  settings,
  signal,
}: {
  plan: string;
  coupon?: string;
  currency?: string;
  interval: string;
  settings?: SubscriptionSettingsUpdate;
  signal?: AbortSignal;
}): Promise<{
  id: string;
  name: string;
  price_original: Record<string, Record<string, number>>;
  price_discounted: Record<string, Record<string, number>>;
  discount_annual: number;
  discount_temporal: number | null;
  coupon_status: string | null;
  coupon_permanent?: boolean;
}> => {
  const res = await fetch(
    `${BASE}/subscription/price/?plan=${encodeURIComponent(plan)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCsrfToken(),
      },
      body: JSON.stringify({
        plan,
        interval,
        currency,
        settings,
        coupon: coupon || undefined,
      }),
      signal,
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export {
  getSubscription,
  getSubscriptionConformance,
  getSubscriptionInAdvance,
  getSubscriptionPlanPrice,
  getSubscriptionPlans,
};
